import React, { useState, useEffect, useMemo } from "react";
import { useDataWithRoute } from "../utils/hooks";
import {
  VsiBubble,
  VsiMedLabel,
  VsiXLText
} from "../styled/App.styled";
import { forecastWithRate, formatStockChartConfig } from "../utils/Helpers";
import { Row, Col, Spinner } from "reactstrap";
import { vsiRed, vsiGreen } from "../utils/themeStyles";
import FlexChart from "../components/FlexChart";
import { useVsiAppContext } from "../stores/VsiApp";

const chartHeight = 335;
const margin = 3;

const chartConfig = {
  colors: [vsiGreen, vsiRed],
  plotOptions: {
    series: {
      dashStyle: "dash"
    }
  },
  yAxis: {
    title: {
      text: "$USD"
    }
  }
};

export default function CloudResourceGroupForecast({ params }) {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("CLOUD");

  const route = useMemo(() => {
    let routeStr = `/api/v6/report/cloud_resource_group_details_proc?client_id=${clientId}&period_id=${periodId}`;
    if (params?.[0]?.value) {
      routeStr += `&resource_group=${encodeURI(params?.[0]?.value)}`;
    }
    return routeStr;
  }, [clientId, periodId, params]);

  const [{ recordsets: [__r0, trendData] }, { isLoading }] = useDataWithRoute(route);

  const { budgetTrend, actualCost, forecastedCost } = useMemo(() => {

    const budgetData = [...(trendData || []), ...forecastWithRate(trendData || [], ["total_cost", "budget"])];

    const allSeriesData = formatStockChartConfig(budgetData || [], [
      {
        type: "areaspline",
        name: "Accumulated Cost",
        field: "total_cost",
        zoneAxis: "x",
        zones: [{
          value: Date.parse(
            trendData?.[trendData.length - 1]?.collection_date?.split?.("T")?.[0]
          )
        },
          {
            dashStyle: "Dash"
          }],
        disableMarker: true,
        colorFilter: {
          name: "forecast"
        }
      },
      {
        type: "spline",
        name: "Monthly Budget",
        field: "budget",
        zoneAxis: "x",
        zones: [{
          value: Date.parse(
            trendData?.[trendData.length - 1]?.collection_date?.split?.("T")?.[0]
          )
        },
          {
            dashStyle: "Dash"
          }],
        disableMarker: true
      }
    ]);

    const lastNonForecastedRef = trendData?.[trendData?.length - 1];

    return {
      budgetTrend: allSeriesData.series || [],
      actualCost: lastNonForecastedRef?.total_cost || 0,
      forecastedCost: budgetData?.find?.(({ collection_date }) => {
        return 0 > new Date(lastNonForecastedRef?.collection_date) - new Date(collection_date);
      })?.total_cost || 0
    };
  }, [trendData]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubble className={`mt-${margin}`}>
      <Row>
        <Col xs={6} md={3} lg={3} className={"d-flex flex-column align-items-start"}>
          <VsiMedLabel>Actual Cost</VsiMedLabel>
          <VsiXLText>
            ${actualCost.toFixed(2)}
          </VsiXLText>
        </Col>
        <Col xs={6} md={3} lg={3} className={"d-flex flex-column align-items-start"}>
          <VsiMedLabel>1 Month Forecast</VsiMedLabel>
          <VsiXLText>
            ${forecastedCost.toFixed(2)}
          </VsiXLText>
        </Col>
      </Row>
      <FlexChart series={budgetTrend}
                 navDisabled={true}
                 disableRangeSelector={true}
                 height={chartHeight}
                 title={"Budget Forecast"}
                 config={chartConfig} />
    </VsiBubble>
  );
}